import React, { useCallback, useMemo } from "react";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import PinLegendClassificationType from "@mapmycustomers/shared/enum/pinLegends/PinLegendClassificationType";
import PinLegendDataLevel from "@mapmycustomers/shared/enum/pinLegends/PinLegendDataLevel";
import PinLegendDatingMethod from "@mapmycustomers/shared/enum/pinLegends/PinLegendDatingMethod";
import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";

import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import loggingService from "@app/util/logging";

import ColorsClassificationEditor from "./ColorsClassificationEditor";
import OptionStyleEditor from "./OptionStyleEditor";
import ShapesClassificationEditor from "./ShapesClassificationEditor";
import ClassificationStyleEditorProps from "./types/ClassificationStyleEditorProps";

interface Props extends Omit<ClassificationStyleEditorProps, "field"> {
  fieldName: string;
}

const StyleEditor: React.FC<Props> = ({
  breakpoints,
  classification,
  colors,
  dataLevel,
  datingMethod,
  defaultColor,
  defaultShape,
  entityType,
  fieldName,
  onBreakpointsChange,
  onClassificationChange,
  onColorChange,
  onDataLevelChange,
  onDatingMethodChange,
  onDefaultColorChange,
  onDefaultShapeChange,
  onError,
  onShapeChange,
  shapes,
  type,
}) => {
  const fieldModel = useMemo(() => getFieldModelByEntityType(entityType), [entityType]);
  const field = useMemo(() => fieldModel.getByName(fieldName), [fieldModel, fieldName]);

  const analytics = useAnalytics();
  const handleDataLevelChange = useCallback(
    (dataLevel: PinLegendDataLevel) => {
      onDataLevelChange?.(dataLevel);
      analytics.clicked(["Access level"], { dataLevel });
    },
    [onDataLevelChange, analytics]
  );
  const handleClassificationChange = useCallback(
    (classification: PinLegendClassificationType) => {
      onClassificationChange?.(classification);
      analytics.clicked(["Classification"], { classification });
    },
    [onClassificationChange, analytics]
  );
  const hangeDatingMethodChange = useCallback(
    (datingMethod: PinLegendDatingMethod) => {
      onDatingMethodChange?.(datingMethod);
      analytics.clicked(["Dating method"], { datingMethod });
    },
    [onDatingMethodChange, analytics]
  );

  if (!field) {
    loggingService.warning(`No such field found in StylePreview: ${entityType}.${fieldName}`);
    return null;
  }

  if (
    field.hasFeature(FieldFeature.NUMERIC) ||
    field.type === FieldType.DATE ||
    field.type === FieldType.DATE_TIME
  ) {
    if (classification === PinLegendClassificationType.SIMPLE) {
      onClassificationChange?.(PinLegendClassificationType.MANUAL);
    }

    return type === PinLegendType.COLOR ? (
      <ColorsClassificationEditor
        breakpoints={breakpoints}
        classification={classification}
        colors={colors ?? []} // we know they're defined...
        dataLevel={dataLevel}
        datingMethod={datingMethod}
        defaultColor={defaultColor!}
        entityType={entityType}
        field={field}
        onBreakpointsChange={onBreakpointsChange}
        onClassificationChange={handleClassificationChange}
        onColorChange={onColorChange}
        onDataLevelChange={handleDataLevelChange}
        onDatingMethodChange={hangeDatingMethodChange}
        onDefaultColorChange={onDefaultColorChange}
        onError={onError}
        type={type}
      />
    ) : (
      <ShapesClassificationEditor
        breakpoints={breakpoints}
        classification={classification}
        dataLevel={dataLevel}
        datingMethod={datingMethod}
        defaultShape={defaultShape!}
        entityType={entityType}
        field={field}
        onBreakpointsChange={onBreakpointsChange}
        onClassificationChange={handleClassificationChange}
        onDataLevelChange={handleDataLevelChange}
        onDatingMethodChange={hangeDatingMethodChange}
        onDefaultShapeChange={onDefaultShapeChange}
        onError={onError}
        onShapeChange={onShapeChange}
        shapes={shapes ?? []} // we know they're defined...
        type={type}
      />
    );
  } else {
    if (classification !== PinLegendClassificationType.SIMPLE) {
      onClassificationChange?.(PinLegendClassificationType.SIMPLE);
    }

    return (
      <OptionStyleEditor
        colors={colors}
        entityType={entityType}
        field={field}
        onColorChange={onColorChange}
        onShapeChange={onShapeChange}
        shapes={shapes}
        type={type}
      />
    );
  }
};

export default StyleEditor;
